import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { AdjustmentFilter } from '@pixi/filter-adjustment';
import { Container, Sprite } from '@pixi/react';
import { useMemo } from 'react';
import { useAppSelector } from '../../../../redux/store';
import { ALL_THEME_BACKGROUND_PROPERTIES } from '../../../../types/themeBackgroundTypes';
import { ALL_THEME_TABLE_PROPERTIES, DEFAULT_TABLE_SHADOW_ALPHA } from '../../../../types/themeTableTypes';
import { isTestEnvironment } from '../../../../utils/utils';
export const TexturedPokerTableDisplay = ({ tableAreaDimensions, feltDimensions, railDimensions, feltTexture, railTexture, shadowTexture }) => {
    const themeTableType = useAppSelector((state) => state.theme.themeTable?.type);
    const feltBrightness = useAppSelector((state) => state.theme.themeTable?.feltBrightness);
    const themeBackgroundType = useAppSelector((state) => state.theme.themeBackground?.type);
    const tableShadowAlpha = useMemo(() => {
        if (!themeBackgroundType) {
            return;
        }
        const themeTableShadowAlpha = ALL_THEME_BACKGROUND_PROPERTIES[themeBackgroundType].tableShadowAlpha;
        return themeTableShadowAlpha !== undefined ? themeTableShadowAlpha : DEFAULT_TABLE_SHADOW_ALPHA;
    }, [themeBackgroundType]);
    const railFilters = useMemo(() => {
        if (!themeTableType) {
            return null;
        }
        const { railTextureBrightnessAmount } = ALL_THEME_TABLE_PROPERTIES[themeTableType];
        if (railTextureBrightnessAmount === 1) {
            return null;
        }
        return [new AdjustmentFilter({ brightness: railTextureBrightnessAmount })];
    }, [themeTableType]);
    const feltFilters = useMemo(() => (feltBrightness !== undefined ? [new AdjustmentFilter({ brightness: feltBrightness })] : null), [feltBrightness]);
    if (themeTableType === undefined) {
        return null;
    }
    const tableCenterX = tableAreaDimensions.w / 2;
    const tableCenterY = tableAreaDimensions.h / 2;
    const railScaleX = railDimensions.w / railTexture.width;
    const railScaleY = railDimensions.h / railTexture.height;
    const railScale = [railScaleX, railScaleY];
    const feltScaleX = feltDimensions.w / feltTexture.width;
    const feltScaleY = feltDimensions.h / feltTexture.height;
    const feltScale = [feltScaleX, feltScaleY];
    const shadowOffsetPx = Math.round(Math.min(tableAreaDimensions.w, tableAreaDimensions.h) * 0.05);
    const shadowScale = [railScaleX * 0.98, railScaleY * 0.98];
    const innerShadowWidth = shadowTexture.width - 2 * shadowOffsetPx;
    const innerShadowHeight = shadowTexture.height - 2 * shadowOffsetPx;
    const shadowElement = tableShadowAlpha ? (_jsxs(Container, { x: tableCenterX + shadowOffsetPx, y: tableCenterY + shadowOffsetPx, scale: shadowScale, alpha: tableShadowAlpha, children: [_jsx(Sprite, { texture: shadowTexture, width: shadowTexture.width, height: shadowTexture.height, x: -shadowTexture.width / 2, y: -shadowTexture.height / 2 }), _jsx(Sprite, { texture: shadowTexture, width: innerShadowWidth, height: innerShadowHeight, x: -innerShadowWidth / 2, y: -innerShadowHeight / 2 })] })) : null;
    const railElement = (_jsx(Container, { x: tableCenterX, y: tableCenterY, scale: railScale, filters: railFilters, children: _jsx(Sprite, { texture: railTexture, width: railTexture.width, height: railTexture.height, x: -railTexture.width / 2, y: -railTexture.height / 2 }) }));
    const feltElement = (_jsx(Container, { x: tableCenterX, y: tableCenterY, scale: feltScale, children: _jsx(Sprite, { texture: feltTexture, width: feltTexture.width, height: feltTexture.height, x: -feltTexture.width / 2, y: -feltTexture.height / 2, filters: feltFilters }) }));
    const pixiData = isTestEnvironment()
        ? {
            type: 'theme-table',
            themeTableType
        }
        : null;
    return (_jsxs(Container, { "pixi-data": pixiData, interactiveChildren: false, children: [shadowElement, railElement, feltElement] }));
};
